import React, { useState } from "react";
import { Upload, Image as Img, message } from "antd";
import ImgCrop from "antd-img-crop";

function ImageUploadWithCropAndWebp({
  fileList,
  setFileList,
  deleteImage = () => {},
  aspectRatio = 1 / 1,
  minWidth = null,
  maxWidth = null
}) {
  const [previewImage, setPreviewImage] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Convert to WebP and return { file, width }
  const convertToWebP = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img = new window.Image();
        img.src = reader.result;

        img.onload = () => {
          const width = img.width;
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("WebP conversion failed"));
                return;
              }
              const webpFile = new File([blob], file.name.replace(/\.\w+$/, ".webp"), {
                type: "image/webp",
              });
              resolve({ webpFile, width });
            },
            "image/webp",
            0.9
          );
        };
      };
    });
  };

  // 🔐 Validation and conversion before uploading
  const beforeUpload = async (file) => {
    try {
      const { webpFile, width } = await convertToWebP(file);

      // Validation
      if (minWidth !== null && width < minWidth) {
        message.error(`Image width must be at least ${minWidth}px. Your image is ${width}px wide.`);
        return Upload.LIST_IGNORE;
      }

      if (maxWidth !== null && width > maxWidth) {
        message.error(`Image width must not exceed ${maxWidth}px. Your image is ${width}px wide.`);
        return Upload.LIST_IGNORE;
      }

      // Add custom flags for tracking
      webpFile.uid = file.uid;
      webpFile.converted = true;

      // Manually set file list with the WebP file
      setFileList([
        {
          uid: webpFile.uid,
          name: webpFile.name,
          status: "done",
          originFileObj: webpFile,
          url: URL.createObjectURL(webpFile),
          converted: true,
        },
      ]);

      return Upload.LIST_IGNORE; // Prevent auto upload and manual file from being added
    } catch (err) {
      console.error(err);
      message.error("Failed to process the image.");
      return Upload.LIST_IGNORE;
    }
  };

  const onChange = ({ fileList }) => {
    // No conversion here — we already handled it in beforeUpload
    setFileList(fileList);
  };

  const onPreview = async (file) => {
    let src = file.url;

    if (!src && file.originFileObj) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }

    setPreviewImage(src);
    setPreviewOpen(true);
  };

  return (
    <div className="mb-10">
      <ImgCrop rotationSlider aspect={aspectRatio}>
        <Upload
          listType="picture-card"
          fileList={fileList}
          onChange={onChange}
          onRemove={deleteImage}
          onPreview={onPreview}
          beforeUpload={beforeUpload}
          customRequest={({ onSuccess }) => {
            setTimeout(() => onSuccess("ok"), 100);
          }}
        >
          {fileList.length < 1 && "+ Upload"}
        </Upload>
      </ImgCrop>

      {previewOpen && (
        <Img
          style={{ display: "none" }}
          src={previewImage}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
          }}
        />
      )}
    </div>
  );
}

export default ImageUploadWithCropAndWebp;
